import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabaseClient';

export interface Profile {
  id: string;
  username: string;
  created_at: string;
  bio?: string;
  avatar_url?: string;
  website?: string;
  twitter?: string;
  linkedin?: string;
  is_writer: boolean;
  is_admin: boolean;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  profileError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, username: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setSession(null);
    setUser(null);
    setProfile(null);
  }, []);

  useEffect(() => {
    let active = true;
    let receivedAuthEvent = false;
    let currentUserId: string | undefined;
    // Keep database requests outside Supabase's auth callback/lock.
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      receivedAuthEvent = true;
      if (!active) return;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      if (currentUserId !== nextSession?.user.id) setProfileLoading(Boolean(nextSession?.user));
      currentUserId = nextSession?.user.id;
      setLoading(false);
    });
    supabase.auth.getSession().then(({ data, error }) => {
      if (!active || receivedAuthEvent) return;
      if (error) console.error('Unable to restore session:', error);
      setSession(data.session);
      setUser(data.session?.user ?? null);
      if (currentUserId !== data.session?.user.id) setProfileLoading(Boolean(data.session?.user));
      currentUserId = data.session?.user.id;
      setLoading(false);
    }).catch((error) => {
      if (!active || receivedAuthEvent) return;
      console.error('Unable to restore session:', error);
      setLoading(false);
    });
    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let active = true;
    setProfile(null);
    setProfileError(null);
    if (!user) { setProfileLoading(false); return; }
    setProfileLoading(true);
    const loadProfile = async () => {
      try {
        let result = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
        if (result.error) throw result.error;
        if (!result.data) {
          // Use the authenticated user directly, rather than stale state from sign-in.
          const { error } = await supabase.from('profiles').insert({
            id: user.id,
            username: user.user_metadata?.username || user.email?.split('@')[0] || 'anonymous',
            is_writer: false,
            is_admin: false,
          });
          // Another tab or the signup trigger may have created the row meanwhile.
          if (error && error.code !== '23505') throw error;
          result = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
          if (result.error) throw result.error;
          if (!result.data) throw new Error('Profile is unavailable');
        }
        if (active) setProfile(result.data);
      } catch (error) {
        console.error('Unable to load profile:', error);
        if (active) setProfileError('Your profile could not be loaded. Please refresh to try again.');
      } finally {
        if (active) setProfileLoading(false);
      }
    };
    void loadProfile();
    return () => { active = false; };
  }, [user]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, username: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
        },
      },
    });

    if (!error && data.user) {
      // After a successful sign-up make sure a profile row exists for the new user.
      const insertResponse = await supabase.from('profiles').insert({
        id: data.user.id,
        username,
        is_writer: false,
        is_admin: false,
      });

      if (insertResponse.error) {
        console.error('Error creating profile after sign-up:', insertResponse.error);
      }
    }

    return { error };
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return;

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) throw error;

    setProfile((prev: Profile | null) => prev ? { ...prev, ...updates } : null);
  };

  const value = {
    user,
    profile,
    session,
    loading: loading || profileLoading,
    profileError,
    signIn,
    signUp,
    signOut,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
